import { toPersistedAdditionalInverters } from '../inverter/additional-inverters';

const filterObjectByKeys = (value, keys) =>
  Object.fromEntries(
    Object.entries(value || {}).filter(([key]) => keys.includes(key))
  );

const isEmptyObject = (value) => !value || Object.keys(value).length === 0;

const normalizeAdditionalInverters = (inverters = []) =>
  toPersistedAdditionalInverters(inverters);

const buildRoofIndexMap = (segments, sortedSegments) => {
  const sortedIndexById = {};
  sortedSegments.forEach((segment, index) => {
    sortedIndexById[segment.id] = index;
  });
  return segments.map((segment) => sortedIndexById[segment.id]);
};

const remapStringingData = (strings, roofIndexMap) => {
  if (!Array.isArray(strings)) return strings;
  return strings
    .map((string) => {
      const panels = (Array.isArray(string?.panels) ? string.panels : [])
        .map((panel) => {
          if (!Array.isArray(panel) || panel.length < 2) return null;
          const roofIndex = Number(panel[0]);
          const panelIndex = Number(panel[1]);
          if (!Number.isFinite(roofIndex) || !Number.isFinite(panelIndex)) {
            return null;
          }
          const mappedRoof = roofIndexMap[roofIndex];
          return [
            Number.isFinite(mappedRoof) ? mappedRoof : roofIndex,
            panelIndex
          ];
        })
        .filter(Boolean);
      return { ...string, panels };
    })
    .filter((string) => string.panels.length > 0);
};

const selectedToCsv = (selected, expectedLength = null) => {
  const values = Array.isArray(selected) ? selected : [];
  const length =
    expectedLength !== null && expectedLength !== undefined
      ? Number(expectedLength) || 0
      : values.length;
  return Array.from({ length }, (_, index) => (values[index] ? 1 : 0)).join(
    ','
  );
};

const processCompanyAdditionalInverters = (values = []) =>
  values
    .map((value) => {
      if (typeof value === 'string') return { id: value, quantity: 1 };
      if (value && typeof value === 'object' && value.id) {
        return { id: value.id, quantity: value.quantity || 1 };
      }
      return null;
    })
    .filter(Boolean);

export const createProposalData = (
  uid,
  initData,
  panels,
  packageData,
  additionalContext = {},
  designerCompanyPanelId,
  designerCompanyInverterId,
  designFrame,
  losses,
  designerCompanyAdditionalInverterIds = [],
  additionalInverters = [],
  inverterQuantity = null,
  hasAutoCalculatedInverterQuantity = false
) => {
  const { segments, obstacles, trees } = initData;
  const { panel, inverter, monitoring, mounting } = packageData;
  const annualUsage =
    Number(
      initData?.annualUsage ??
        initData?.annual_usage ??
        initData?.designData?.annualUsage ??
        initData?.design_data?.annualUsage ??
        0
    ) || 0;
  const primaryInverterAutoQuantity =
    initData?.primaryInverterAutoQuantity !== undefined &&
    initData?.primaryInverterAutoQuantity !== null
      ? initData.primaryInverterAutoQuantity
      : !hasAutoCalculatedInverterQuantity;
  const normalizedAdditionalInverters =
    normalizeAdditionalInverters(additionalInverters);
  const context = filterObjectByKeys(additionalContext, [
    'panelType',
    'inverter',
    'defaultBtnView',
    'companyPanelID',
    'companyInverterID',
    'setback'
  ]);
  if ('panelType' in context && !designFrame) {
    context.panel = context.panelType;
  }

  const segmentsInDesignerOrder = [...segments].filter(
    (segment) => !Number.isNaN(Number(segment.azimuth))
  );
  const sortedSegments = [...segmentsInDesignerOrder].sort(
    (left, right) => right.tsrf[12] - left.tsrf[12]
  );
  const roofIndexMap = buildRoofIndexMap(
    segmentsInDesignerOrder,
    sortedSegments
  );
  const sortedPanels = sortedSegments.map((segment) =>
    panels.find((candidate) => segment.id === candidate.id)
  );
  const strings = remapStringingData(
    initData?.stringingData ?? initData?.stringing_data ?? null,
    roofIndexMap
  );
  const annualProduction =
    Number(initData?.totalAnnualKwh ?? initData?.total_annual_kwh ?? 0) || 0;
  const panelsFromStrings = Array.isArray(strings)
    ? strings.reduce(
        (total, string) => total + (string?.panels?.length || 0),
        0
      )
    : 0;
  const totalPanels =
    Number(initData?.selectedPanelCount ?? panelsFromStrings ?? 0) || 0;
  const systemSize = totalPanels * ((Number(panel?.power || 0) || 0) / 1000);
  const offsetFallback =
    Number(
      initData?.offset ??
        initData?.summary?.offset ??
        initData?.designData?.summary?.offset ??
        initData?.design_data?.summary?.offset ??
        0
    ) || 0;
  const offset =
    annualUsage > 0
      ? Math.floor((annualProduction / annualUsage) * 100)
      : offsetFallback;
  const designData = {
    segments: sortedSegments,
    obstacles,
    trees,
    panels: sortedPanels,
    panel,
    inverter,
    inverterQuantity: inverterQuantity ?? packageData.inverterQuantity ?? 1,
    hasAutoCalculatedInverterQuantity,
    primaryInverterAutoQuantity,
    useTargetProduction: Boolean(initData?.useTargetProduction),
    targetProductionKwh: initData?.targetProductionKwh ?? 0,
    maxDcAcRatio: initData?.maxDcAcRatio ?? initData?.max_dc_ac_ratio ?? 1.5,
    onlySelectedPanels: Boolean(
      initData?.onlySelectedPanels ?? initData?.only_selected_panels ?? false
    ),
    summary: {
      solarProduction: annualProduction,
      solar_production: annualProduction,
      systemSize,
      system_size: systemSize,
      totalPanels,
      total_panels: totalPanels,
      offset
    },
    monthlyProduction:
      initData?.totalMonthlyKwh ?? initData?.total_monthly_kwh ?? [],
    annual_clipping_kwh:
      initData?.totalClippingKwh ?? initData?.total_clipping_kwh ?? 0,
    monthly_clipping_kwh:
      initData?.totalMonthlyClippingKwh ??
      initData?.total_monthly_clipping_kwh ??
      [],
    total_annual_kwh: annualProduction,
    total_clipping_kwh:
      initData?.totalClippingKwh ?? initData?.total_clipping_kwh ?? 0,
    dc_ac_ratio: initData?.dcAcRatio ?? initData?.dc_ac_ratio ?? null,
    stringing_data: strings,
    inverters_used: initData?.invertersUsed ?? initData?.inverters_used ?? null,
    stringing_panel_index_mode: 'all',
    additional_inverters: normalizedAdditionalInverters,
    wiring: initData?.wiring ?? null,
    wiringConfigured: Boolean(initData?.wiringConfigured),
    powerOptimizer: initData?.powerOptimizer ?? null,
    monitoring,
    mounting,
    losses,
    ...context
  };
  const designs = sortedPanels.map((panelLayout) => {
    const { id, azimuth, horizontal, vertical } = panelLayout;
    const segment = segments.find((candidate) => candidate.id === id);
    const { acMonthly, solarAccess, tsrf, tof } = segment;
    let validAzimuth = Number(azimuth);
    if (validAzimuth < 0) validAzimuth += 360;
    if (validAzimuth >= 360) validAzimuth -= 360;
    return {
      azimuth: validAzimuth.toFixed(2),
      panel_orientation: Number(segment.panelOrientation).toFixed(13),
      horizontal: horizontal.points,
      horizontal_size: [
        Math.round(horizontal.height),
        Math.round(horizontal.width)
      ].join(),
      horizontal_selected: selectedToCsv(
        horizontal.selected,
        horizontal.points.length
      ),
      vertical: vertical.points,
      vertical_size: [
        Math.round(vertical.height),
        Math.round(vertical.width)
      ].join(),
      vertical_selected: selectedToCsv(
        vertical.selected,
        vertical.points.length
      ),
      ac_monthly: acMonthly.map((item) => Number(item).toFixed(2)),
      solar_access: solarAccess.map((item) => Number(item).toFixed(2)),
      tsrf: tsrf.map((item) => Number(item).toFixed(2)),
      tof: tof.map((item) => Number(item).toFixed(2)),
      panelAxis: segment?.panelAxis ?? panelLayout?.panelAxis ?? null,
      annual_kwh: segment?.annual_kwh ?? 0,
      clipping_kwh: segment?.clipping_kwh ?? 0,
      monthly_clipping_kwh: segment?.monthly_clipping_kwh ?? undefined,
      stringing_data: segment?.stringing_data ?? null
    };
  });

  const companyPanelId =
    context.companyPanelID || designerCompanyPanelId || null;
  const companyInverterId =
    context.companyInverterID || designerCompanyInverterId || null;
  const companyAdditionalInverterIds =
    context.companyAdditionalInverterIDs?.length > 0
      ? context.companyAdditionalInverterIDs
      : designerCompanyAdditionalInverterIds;
  const companyAdditionalInverters = processCompanyAdditionalInverters(
    companyAdditionalInverterIds
  );
  if (!isEmptyObject(context)) {
    context.companyPanelID = companyPanelId;
    context.companyInverterID = companyInverterId;
    context.companyAdditionalInverterIDs = companyAdditionalInverterIds;
    context.panel = companyPanelId ? null : panel;
    context.inverter = companyInverterId ? null : inverter;
    context.additional_inverters = companyAdditionalInverterIds?.length
      ? []
      : additionalInverters;
  }

  return {
    lead: uid,
    panel: companyPanelId ? null : panel.id,
    company_panel: companyPanelId,
    inverter: companyInverterId ? null : inverter.id,
    company_inverter: companyInverterId,
    additional_inverters: companyAdditionalInverterIds?.length
      ? []
      : normalizedAdditionalInverters,
    company_additional_inverters: companyAdditionalInverters,
    monitoring: monitoring ? monitoring.id : null,
    mounting: mounting ? mounting.id : null,
    designs,
    design_data: designData,
    input_log: !isEmptyObject(context)
      ? Object.fromEntries(
          Object.entries(context).filter(
            ([name]) => name !== 'panelType' && name !== 'defaultBtnView'
          )
        )
      : {}
  };
};
